import { all, takeLatest, put, select, call } from 'redux-saga/effects';
import { scopesByOperationId } from '@msi-alta/helium-client/scopes';

import { heliumReceive, heliumSend } from 'utils/helpers';
import { verifyScope } from 'utils/redirects';
import { selectActiveScopes } from 'global/accessToken/selectors';

import { locationsActions } from '../slices/locationsSlice';
import {
  clearGlobalSignInWorkflow,
  describeVisitorWorkflowConfig,
  describeVisitorWorkflowFields,
} from '../slices/visitorWorkflowsSlice';
import { ImmutableRootState } from '../store';
import { Location, LocationWorkflow } from '../../types/locationTypes';

interface HeliumResponse<T> {
  data?: T[];
}

interface LocationsStateSelector {
  getIn: (path: Array<string | number>) => unknown;
}

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message?: unknown }).message || '');
  }
  return '';
};

function* fetchLocationsSaga(
  action: ReturnType<typeof locationsActions.fetchLocations>,
): Generator {
  const { orgId, status } = action.payload;
  try {
    yield put(
      locationsActions.setLoading({
        key: 'fetchLocationsLoading',
        value: true,
      }),
    );
    const res = (yield call(heliumReceive, 'listAllLocations', [orgId], {
      queryStringParams: { status },
    })) as HeliumResponse<Location>;
    yield put(locationsActions.setLocations(res.data || []));
  } catch (e: unknown) {
    yield put(locationsActions.setError(getErrorMessage(e)));
  } finally {
    yield put(
      locationsActions.setLoading({
        key: 'fetchLocationsLoading',
        value: false,
      }),
    );
  }
}

function* describeLocationSaga(
  action: ReturnType<typeof locationsActions.describeLocation>,
): Generator {
  const { orgId, locationId, global } = action.payload;

  if (locationId === -1) {
    const mockAllLocation: Location = {
      id: -1,
      name: 'All Locations',
      description: null,
      address: null,
      address2: null,
      city: null,
      region: null,
      postalCode: null,
      country: null,
      phone: null,
      timezone: null,
      language: null,
      createdAt: null,
      updatedAt: null,
      status: 1,
    };
    yield put(
      locationsActions.setSelectedLocation({
        data: mockAllLocation,
        global,
      }),
    );
    return;
  }

  try {
    yield put(
      locationsActions.setLoading({
        key: 'describeLocationLoading',
        value: true,
      }),
    );
    const res = (yield call(heliumReceive, 'describeAllLocations', [
      orgId,
      locationId,
    ])) as HeliumResponse<Location>;
    yield put(
      locationsActions.setSelectedLocation({
        data: res.data?.[0] as Location,
        global,
      }),
    );
  } catch (e: unknown) {
    yield put(locationsActions.setError(getErrorMessage(e)));
  } finally {
    yield put(
      locationsActions.setLoading({
        key: 'describeLocationLoading',
        value: false,
      }),
    );
  }
}

function* createLocationSaga(
  action: ReturnType<typeof locationsActions.createLocation>,
): Generator {
  const { orgId, location } = action.payload;
  try {
    yield put(
      locationsActions.setLoading({
        key: 'createLocationLoading',
        value: true,
      }),
    );
    yield call(heliumSend, 'insertLocation', [orgId], location);
    yield put(locationsActions.fetchLocations({ orgId }));
  } catch (e: unknown) {
    yield put(locationsActions.setError(getErrorMessage(e)));
  } finally {
    yield put(
      locationsActions.setLoading({
        key: 'createLocationLoading',
        value: false,
      }),
    );
  }
}

function* updateLocationSaga(
  action: ReturnType<typeof locationsActions.updateLocation>,
): Generator {
  const { orgId, locationId, location } = action.payload;
  try {
    yield put(
      locationsActions.setLoading({
        key: 'updateLocationLoading',
        value: true,
      }),
    );
    yield call(heliumSend, 'updateLocation', [orgId, locationId], location);

    yield put(locationsActions.describeLocation({ orgId, locationId }));

    const state = (yield select((s: ImmutableRootState) =>
      s.get('locations'),
    )) as LocationsStateSelector;
    const globalLocationId = state.getIn(['globalLocation', 'id']) as
      | number
      | undefined;
    if (locationId === globalLocationId) {
      yield put(
        locationsActions.describeLocation({ orgId, locationId, global: true }),
      );
    }
  } catch (e: unknown) {
    yield put(locationsActions.setError(getErrorMessage(e)));
  } finally {
    yield put(
      locationsActions.setLoading({
        key: 'updateLocationLoading',
        value: false,
      }),
    );
  }
}

function* describeLocationWorkflowSaga(
  action: ReturnType<typeof locationsActions.describeLocationWorkflow>,
): Generator {
  const { orgId, locationId, global } = action.payload;

  if (locationId === -1) {
    const allLocationWorkflow: LocationWorkflow = {
      site: 'All locations',
      SignInWorkflowId: -1,
      SignOutWorkflowId: -1,
      KioskInWorkflowId: -1,
      KioskOutWorkflowId: -1,
    };
    yield put(
      locationsActions.setSelectedLocationWorkflow({
        data: allLocationWorkflow,
        global,
      }),
    );
    yield put(clearGlobalSignInWorkflow());
    return;
  }

  try {
    yield put(
      locationsActions.setLoading({
        key: 'describeLocationWorkflowLoading',
        value: true,
      }),
    );
    const res = (yield call(heliumReceive, 'locationConfig', [
      orgId,
      locationId,
    ])) as HeliumResponse<LocationWorkflow>;
    const workflow = res.data?.[0] as LocationWorkflow;

    yield put(
      locationsActions.setSelectedLocationWorkflow({ data: workflow, global }),
    );

    if (global && workflow?.SignInWorkflowId) {
      // The workflow config / field-config endpoints 403 for limited-scope
      // roles (e.g. location-read-only), so only fetch them when the user has a
      // scope helium's routes accept. Mirrors the location/redflag guards.
      const activeScopes = yield select(selectActiveScopes());

      if (
        verifyScope(
          activeScopes,
          scopesByOperationId.getVisitorWorkflowConfig,
          orgId,
        )
      ) {
        yield put(
          describeVisitorWorkflowConfig({
            orgId,
            visitorWorkflowId: workflow.SignInWorkflowId,
            global: true,
          }),
        );
      }

      if (
        verifyScope(
          activeScopes,
          scopesByOperationId.getVisitorWorkflowFields,
          orgId,
        )
      ) {
        yield put(
          describeVisitorWorkflowFields({
            orgId,
            visitorWorkflowId: workflow.SignInWorkflowId,
            global: true,
          }),
        );
      }
    }
  } catch (e: unknown) {
    yield put(locationsActions.setError(getErrorMessage(e)));
  } finally {
    yield put(
      locationsActions.setLoading({
        key: 'describeLocationWorkflowLoading',
        value: false,
      }),
    );
  }
}

function* updateLocationWorkflowSaga(
  action: ReturnType<typeof locationsActions.updateLocationWorkflow>,
): Generator {
  const { orgId, locationId, workflow } = action.payload;
  try {
    yield put(
      locationsActions.setLoading({
        key: 'updateLocationWorkflowLoading',
        value: true,
      }),
    );
    yield call(
      heliumSend,
      'updateLocationConfig',
      [orgId, locationId],
      workflow,
    );

    yield put(locationsActions.describeLocationWorkflow({ orgId, locationId }));

    const state = (yield select((s: ImmutableRootState) =>
      s.get('locations'),
    )) as LocationsStateSelector;
    const globalLocationId = state.getIn(['globalLocation', 'id']) as
      | number
      | undefined;
    if (locationId === globalLocationId) {
      yield put(
        locationsActions.describeLocationWorkflow({
          orgId,
          locationId,
          global: true,
        }),
      );
    }
  } catch (e: unknown) {
    yield put(locationsActions.setError(getErrorMessage(e)));
  } finally {
    yield put(
      locationsActions.setLoading({
        key: 'updateLocationWorkflowLoading',
        value: false,
      }),
    );
  }
}

export default function* locationsSaga(): Generator {
  yield all([
    takeLatest(locationsActions.fetchLocations.type, fetchLocationsSaga),
    takeLatest(locationsActions.describeLocation.type, describeLocationSaga),
    takeLatest(locationsActions.createLocation.type, createLocationSaga),
    takeLatest(locationsActions.updateLocation.type, updateLocationSaga),
    takeLatest(
      locationsActions.describeLocationWorkflow.type,
      describeLocationWorkflowSaga,
    ),
    takeLatest(
      locationsActions.updateLocationWorkflow.type,
      updateLocationWorkflowSaga,
    ),
  ]);
}
